import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useDispatch } from 'react-redux';
import { oneOneOneReactQueryKeys } from 'screens/oneOnOneRabbit/InstancePage/constants/oneOnOneReactQueryKeys';
import {
  useOneOnOneAgendaLibraryThemeType,
  useOneOnOneAgendaLibraryType,
  setAgendaLibraryAddedAgendaIds,
  useOneOnOneAgendaLibraryAddedAgendaIds,
  useOneOnOneAgendaLibraryLifeCycleMomentType,
} from 'screens/oneOnOneRabbit/InstancePage/slice/OneOnOneInstancePageSlice';
import useOneOnOnePageParams from 'screens/oneOnOneRabbit/hooks/useOneOnOnePageParams';

import { Check, Search } from 'assets/assetsV1/iconComponents';

import { sendClickedOnAddAdminAgendaEvent, sendClickedOnOneOnOneAddAgendaFromLibrary } from 'constants/mixpanelHelper';

import { Button } from 'uiToolkitV1/Button';
import { ChakraStyleInput } from 'uiToolkitV1/InputV2/ChakraStyleInput';
import { Select } from 'uiToolkitV1/Select';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { OneOnOneAgendaCreateOrUpdateI } from '../../Agenda/hooks/dataHooks';
import { OneOnOneAgendaLibraryI } from '../interface';
import { addAgendaFromLibraryToInstance } from '../services';
import '../styles/index.css';

interface Props {
  data: OneOnOneAgendaLibraryI;
  isLoading: boolean;
  isSettingsPage?: boolean;
  onAgendaClick?: ({ id, agendaText }: { id: number; agendaText: string }) => void;
}

const AgendaLibraryAgGrid = ({ data, isLoading, isSettingsPage, onAgendaClick }: Props) => {
  const dispatch = useDispatch();
  const { masterId, instanceId } = useOneOnOnePageParams();
  const gridRef = useRef<AgGridReact>(null);
  const [sortDir, setSortDir] = useState('asc');
  const [searchText, setSearchText] = useState('');

  const queryClient = useQueryClient();

  const selectedType = useOneOnOneAgendaLibraryType();

  const addedAgendaIds = useOneOnOneAgendaLibraryAddedAgendaIds();

  const selectedThemeType = useOneOnOneAgendaLibraryThemeType();
  const selectedLifeCycleType = useOneOnOneAgendaLibraryLifeCycleMomentType();

  const filteredDataAsPerThemeType = useMemo(
    () => data?.filter((item) => item.theme === selectedThemeType) ?? [],
    [data, selectedThemeType]
  );

  const filteredDataAsPerLifeCycleType = useMemo(
    () => data?.filter((item) => item.lifecycleMoment === selectedLifeCycleType) ?? [],
    [data, selectedLifeCycleType]
  );

  const handleAddToAgenda = async (params: { id: number; agendaText: string }) => {
    dispatch(
      setAgendaLibraryAddedAgendaIds({
        addedAgendaId: params.id,
      })
    );
    if (isSettingsPage) {
      sendClickedOnAddAdminAgendaEvent({
        eventProperties: {
          type: 'Library',
          category: selectedType,
          topic: selectedType === 'THEME' ? selectedThemeType : selectedLifeCycleType,
        },
      });
      onAgendaClick?.({
        id: params.id,
        agendaText: params.agendaText,
      });
      return;
    }

    sendClickedOnOneOnOneAddAgendaFromLibrary({
      eventProperties: {
        type: 'Library',
        category: selectedType,
        topic: selectedType === 'THEME' ? selectedThemeType : selectedLifeCycleType,
        instance_id: instanceId,
        source: 'from library',
      },
    });
    const addedAgenda = await addAgendaFromLibraryToInstance({
      masterId,
      instanceId,
      agendaText: params.agendaText,
      libraryAgendaId: params.id,
    });

    if (addedAgenda) {
      queryClient.setQueryData(
        [oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId],
        (old: OneOnOneAgendaCreateOrUpdateI[]) => [...old, addedAgenda]
      );
    }
  };

  useEffect(() => {
    setSearchText('');
  }, [selectedType, selectedThemeType, selectedLifeCycleType]);

  const sortAsc = useCallback(() => {
    gridRef.current?.api?.applyColumnState({
      state: [{ colId: 'agendaText', sort: 'asc' }],
      defaultState: { sort: null },
    });
  }, []);

  const sortDesc = useCallback(() => {
    gridRef.current?.api?.applyColumnState({
      state: [{ colId: 'agendaText', sort: 'desc' }],
      defaultState: { sort: null },
    });
  }, []);

  const sort = useCallback(
    (value) => {
      if (value === 'asc') {
        sortAsc();
      } else {
        sortDesc();
      }
    },
    [sortAsc, sortDesc]
  );

  const sortingOptions = useMemo(
    () => [
      {
        label: 'A to Z',
        value: 'asc',
      },
      {
        label: 'Z to A',
        value: 'desc',
      },
    ],
    []
  );

  const onSelectionChanged = useCallback(() => {
    sort(sortDir);
  }, [sort, sortDir]);

  const columnDefs: ColDef[] = [
    {
      field: 'agendaText',
      wrapText: true,
      autoHeight: true,
      cellRenderer: (params) => (
        <Flex w="100%" h="100%" alignItems="center">
          <TextBodyMedium>{params?.value}</TextBodyMedium>
        </Flex>
      ),
      flex: 1,
      getQuickFilterText: (params) => params?.data?.agendaText,
    },
    {
      field: 'addToAgenda',
      valueGetter: (params) => ({
        id: params?.data?.id,
        agendaText: params?.data?.agendaText,
      }),
      cellRenderer: (params) => {
        const isAdded = addedAgendaIds?.includes(params?.value?.id);
        return (
          <Flex h="100%" alignItems="center">
            {isAdded ? (
              <Flex alignItems="center" ml="46px">
                <Check boxSize="24px" mr="4px" color="#44812F" />
                <TextBodyMedium bold color="#44812F">
                  Added
                </TextBodyMedium>
              </Flex>
            ) : (
              <Button variant="outlined" onClick={() => handleAddToAgenda(params?.value)}>
                Add to Agenda
              </Button>
            )}
          </Flex>
        );
      },
      width: 180,
    },
  ];

  if (isLoading) {
    return null;
  }

  return (
    <>
      <Flex alignItems="center" padding="10px">
        <ChakraStyleInput
          leftComponent={<Search />}
          placeholder="Search Questions"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <label style={{ color: '#999999', display: 'flex', marginLeft: '10px' }}>
          <span style={{ minWidth: 'max-content' }}>Sort by:</span>
          {/* @ts-ignore */}
          <Select
            handleChange={({ value }) => {
              sort(value);
              setSortDir(value);
            }}
            options={sortingOptions}
            isSearchable={false}
            withBackground={false}
            formControlProps={{
              minWidth: '75px',
            }}
            minWidth="max-content"
            selectProps={{
              defaultValue: sortingOptions[0],
            }}
          />
        </label>
      </Flex>
      <div
        style={{
          height: 'calc(100% - 66px)',
          width: '100%',
          padding: '0 8px',
        }}
        className="ag-theme-alpine"
      >
        <AgGridReact
          ref={gridRef}
          columnDefs={columnDefs}
          rowData={selectedType === 'THEME' ? filteredDataAsPerThemeType : filteredDataAsPerLifeCycleType}
          animateRows={false}
          headerHeight={0}
          cacheQuickFilter
          rowHeight={72}
          quickFilterText={searchText}
          defaultColDef={{
            autoHeight: true,
          }}
          rowClass="agenda-library-row"
          onSelectionChanged={onSelectionChanged}
          onGridReady={() => {
            sort(sortDir);
          }}
          suppressContextMenu
          rowStyle={{ cursor: 'default' }}
        />
      </div>
    </>
  );
};

export { AgendaLibraryAgGrid };
